import { z } from "zod";
import { BusinessStatusSchema } from "./pro.js";
// ─────────────────────────────────────────────────────────────────────────────
// Lot Recentrage — EPIC R1 : vérification des comptes pro (docs/RECENTRAGE_SPEC.md)
// US-R12→R15 — l'échange admin ↔ pro : demande de pièces complémentaires,
// réponse du pro, motif de rejet (dette US-17).
//
// AUCUN nouveau statut. Le dossier reste PENDING_VERIFICATION pendant tout
// l'échange : un statut INFO_REQUESTED ferait répondre 409 à POST /v1/uploads/kbis
// (qui exige PENDING_VERIFICATION) au moment précis où l'admin réclame un
// document, et l'instance N-1 ignorerait la valeur d'enum pendant la fenêtre
// rolling (AGENTS.md §12). L'état affiché est donc DÉRIVÉ du dernier message —
// c'est ProVerificationState, calculé côté serveur, jamais stocké.
//
// RÈGLE — la clé de stockage d'une pièce jointe est PRIVÉE : aucun DTO ci-dessous
// ne l'expose, seulement `hasAttachment`. Le contenu se lit par la route admin
// dédiée, comme le KBIS.
// ─────────────────────────────────────────────────────────────────────────────
export const ProVerificationAuthorRoleSchema = z.enum(["admin", "pro"]);
export const ProVerificationMessageKindSchema = z.enum([
    /** L'admin réclame une pièce ou une précision (US-R14). */
    "info_request",
    /** Le pro répond, éventuellement avec une pièce jointe (US-R15). */
    "response",
    /** Motif du refus, communiqué au déclarant (US-R12, dette US-17). */
    "rejection",
]);
/**
 * État DÉRIVÉ d'un dossier — pour le bandeau du pro (US-R13) et la file admin
 * (US-R11). Ce n'est pas une colonne : il se calcule à partir de
 * `BusinessStatus` + du dernier message de l'échange.
 */
export const ProVerificationStateSchema = z.enum([
    /** Vérifié : aucun bandeau, rien à faire. */
    "active",
    /** En attente de revue, rien n'a été demandé au pro. */
    "pending",
    /** L'admin attend une pièce : la balle est chez le pro. */
    "info_requested",
    /** Le pro a répondu : la balle est chez l'admin. */
    "responded",
    /** Refusé : motif disponible, recours possible. */
    "rejected",
]);
export const ProVerificationMessageDTOSchema = z.object({
    id: z.string().uuid(),
    authorRole: ProVerificationAuthorRoleSchema,
    kind: ProVerificationMessageKindSchema,
    body: z.string(),
    /** Jamais la clé de stockage — seulement sa présence. */
    hasAttachment: z.boolean(),
    createdAt: z.string(),
});
/** Ce que le pro voit de son propre dossier — `GET /v1/pro/verification`. */
export const ProVerificationDTOSchema = z.object({
    status: BusinessStatusSchema,
    state: ProVerificationStateSchema,
    /** Motif du dernier refus, ou null. */
    rejectReason: z.string().nullable(),
    /** Un justificatif a-t-il déjà été déposé (US-17) ? */
    hasKbis: z.boolean(),
    /** L'échange complet, du plus ancien au plus récent. */
    messages: z.array(ProVerificationMessageDTOSchema),
    /**
     * false quand une réponse est déjà en attente de revue, ou que le plafond
     * journalier est atteint : coupe la boucle « rejeté → je réponds → rejeté »
     * (US-R15). Le client masque le formulaire ; le serveur refuse de toute façon.
     */
    canRespond: z.boolean(),
});
// ── Entrées (validées à la frontière — AGENTS.md §10) ────────────────────────
/** `POST /v1/admin/pro/:id/request-info` — l'admin dit ce qui manque (US-R14). */
export const RequestProInfoInputSchema = z.object({
    body: z.string().trim().min(10).max(1000),
});
/**
 * `POST /v1/admin/pro/:id/reject` — le motif est OBLIGATOIRE et désormais
 * persisté : sans lui, le pro ne peut pas savoir pourquoi il est invisible
 * (US-R12/R13). Bornes inchangées pour ne rien casser des appels existants.
 */
export const RejectProInputSchema = z.object({
    reason: z.string().trim().min(3).max(500),
});
/** `POST /v1/pro/verification/response` — le formulaire du pro (US-R15). */
export const ProVerificationResponseInputSchema = z.object({
    body: z.string().trim().min(10).max(1000),
    /**
     * Pièce jointe optionnelle — binaire base64, même transport et mêmes gardes
     * que `/v1/uploads/kbis` (magic bytes + 5 Mio après décodage, PDF ou image).
     */
    attachment: z.string().min(1).max(12_000_000).optional(),
});
//# sourceMappingURL=proVerification.js.map